import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  CreditCardIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  CheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface PlanHistoryEvent {
  date: number;
  type: 'initial' | 'upgrade' | 'downgrade';
  fromPlan: string | null;
  toPlan: string | null;
  refundAmount: number | null;
}

interface SubscriptionData {
  hasSubscription: boolean;
  status: string | null;
  planName: string | null;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: number | null;
  subscriptionPrice: number | null;
  stripeCustomerId: string | null;
  managedDirectly?: boolean;
}

interface PlanPreview {
  currentPlanName: string;
  newPlanName: string;
  newMonthlyPrice: number;
  nextBillingDate: number;
  isUpgrade: boolean;
  // Upgrade-only
  proratedAmount?: number;
  // Downgrade-only
  daysInPeriod?: number;
  daysUsed?: number;
  daysRemaining?: number;
  oldDailyRateCents?: number;
  newDailyRateCents?: number;
  oldPlanCostCents?: number;
  newPlanCostCents?: number;
  totalFairCostCents?: number;
  amountPaid?: number;
  refundAmount?: number;
  refundIsAutomatic?: boolean; // true = auto card refund; false = team handles manually
}

const PLANS = [
  {
    key: 'STARTER',
    label: 'Starter',
    price: 4900,
    description: 'Up to 25 properties',
    features: [
      '25 properties max',
      'Property groups and units',
      'Resident and vendor management',
      'Invoices, PDF receipts, and online payments',
      'Maintenance, documents, events, expenses, and cash flow',
    ],
  },
  {
    key: 'GROWTH',
    label: 'Growth',
    price: 9900,
    description: 'Up to 100 properties',
    features: [
      '100 properties max',
      'Property groups and units',
      'Resident and vendor management',
      'Invoices, PDF receipts, and online payments',
      'Maintenance, documents, events, expenses, and cash flow',
    ],
  },
  {
    key: 'PROFESSIONAL',
    label: 'Professional',
    price: 17900,
    description: 'Up to 500 properties',
    features: [
      '500 properties max',
      'Property groups and units',
      'Resident and vendor management',
      'Invoices, PDF receipts, and online payments',
      'Maintenance, documents, events, expenses, and cash flow',
    ],
  },
  {
    key: 'ENTERPRISE',
    label: 'Enterprise',
    price: 25000,
    description: 'Unlimited properties',
    features: [
      'Unlimited properties',
      'Property groups and units',
      'Resident and vendor management',
      'Invoices, PDF receipts, and online payments',
      'Maintenance, documents, events, expenses, and cash flow',
    ],
  },
];

const Subscription: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [managingPortal, setManagingPortal] = useState(false);

  const [planHistory, setPlanHistory] = useState<PlanHistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [changing, setChanging] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeSuccess, setChangeSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
    fetchSubscriptionDetails();
    fetchPlanHistory();
  }, [user, navigate]);

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await api.get<SubscriptionData>('/api/stripe/subscription-status');
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get<{ events: PlanHistoryEvent[] }>('/api/stripe/plan-history');
      setPlanHistory(response.data.events || []);
    } catch (error) {
      console.error('Error fetching plan history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setManagingPortal(true);
      const response = await api.post<{ url: string }>('/api/stripe/create-portal-session');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      alert('Failed to open subscription management portal. Please try again.');
      setManagingPortal(false);
    }
  };

  const handlePreviewPlanChange = async (newPlanKey: string) => {
    setPreviewLoading(newPlanKey);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await api.post<PlanPreview>('/api/stripe/preview-plan-change', { newPlanName: newPlanKey });
      setPreview(res.data);
      setShowConfirmModal(true);
    } catch (err: any) {
      setPreviewError(err?.response?.data?.error || 'Failed to load plan change preview. Please try again.');
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!preview) return;
    setChanging(true);
    setChangeError(null);
    try {
      const result = await api.post<{
        refundOwedAmount?: number;
        refundIssuedAmount?: number;
        refundPendingAmount?: number;
        refundIssued?: boolean;
        refundPending?: boolean;
      }>('/api/stripe/change-plan', { newPlanName: preview.newPlanName });
      setShowConfirmModal(false);
      // Refresh history after a successful plan change
      fetchPlanHistory();
      if (preview.isUpgrade) {
        setChangeSuccess(`Successfully upgraded to ${getPlanLabel(preview.newPlanName)}! Your card has been charged the prorated amount.`);
      } else {
        const {
          refundIssuedAmount = 0,
          refundPendingAmount = 0,
          refundIssued = false,
          refundPending = false,
        } = result.data ?? {};

        const parts: string[] = [`Switched to ${getPlanLabel(preview.newPlanName)}.`];
        if (refundIssued && refundIssuedAmount > 0) {
          parts.push(`A refund of ${formatCents(refundIssuedAmount)} has been issued to your original payment method and should appear within 5–10 business days.`);
        }
        if (refundPending && refundPendingAmount > 0) {
          parts.push(`An additional ${formatCents(refundPendingAmount)} is owed but could not be processed automatically — our team has been notified and will issue it manually within 1–2 business days.`);
        }
        setChangeSuccess(parts.join(' '));
      }
      await fetchSubscriptionDetails();
    } catch (err: any) {
      setChangeError(err?.response?.data?.error || 'Plan change failed. Please try again.');
    } finally {
      setChanging(false);
    }
  };

  const formatDate = (timestamp: number) =>
    new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const getPlanLabel = (key: string) => PLANS.find(p => p.key === key.toUpperCase())?.label || key;

  const currentPlanKey = (subscription?.planName || '').toUpperCase();
  const currentPlanIndex = PLANS.findIndex(p => p.key === currentPlanKey);

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    const configs: Record<string, { color: string; icon: any; text: string }> = {
      active: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircleIcon, text: 'Active' },
      past_due: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: ClockIcon, text: 'Past Due' },
      canceled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircleIcon, text: 'Canceled' },
      trialing: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: ClockIcon, text: 'Trial' },
    };
    const config = configs[status] || configs.active;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#22d3ee] mx-auto" />
          <p className="mt-4 text-white/60">Loading subscription details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Subscription</h1>
        <p className="text-white/50 mt-1">Manage your BlockBoard plan and billing</p>
      </div>

      {changeSuccess && (
        <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25">
          <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <p className="text-emerald-400 text-sm">{changeSuccess}</p>
          <button onClick={() => setChangeSuccess(null)} className="ml-auto text-white/30 hover:text-white/60 flex-shrink-0">
            <XCircleIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {previewError && (
        <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-red-500/10 border border-red-500/25">
          <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{previewError}</p>
          <button onClick={() => setPreviewError(null)} className="ml-auto text-white/30 hover:text-white/60 flex-shrink-0">
            <XCircleIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {!subscription?.hasSubscription ? (
        <div className="floating-card text-center py-16">
          <CreditCardIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Active Subscription</h2>
          <p className="text-white/50 mb-6">You don't have an active subscription yet.</p>
          <button
            onClick={() => navigate('/pricing')}
            className="btn-primary"
          >
            View Plans
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Current Plan Summary */}
          <div className="floating-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Current Plan</h2>
              {getStatusBadge(subscription.status)}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-white/50 text-sm mb-1">Plan</p>
                <p className="text-2xl font-bold text-white">{getPlanLabel(currentPlanKey)}</p>
              </div>
              <div>
                <p className="text-white/50 text-sm mb-1">Monthly Price</p>
                <p className="text-2xl font-bold text-white">
                  {subscription.subscriptionPrice
                    ? formatCents(subscription.subscriptionPrice)
                    : PLANS.find(p => p.key === currentPlanKey)
                      ? formatCents(PLANS.find(p => p.key === currentPlanKey)!.price)
                      : 'N/A'}
                  <span className="text-sm text-white/40 font-normal">/mo</span>
                </p>
              </div>
              {subscription.currentPeriodEnd && (
                <div>
                  <p className="text-white/50 text-sm mb-1">
                    {subscription.cancelAtPeriodEnd ? 'Ends On' : 'Next Billing'}
                  </p>
                  <p className="text-lg text-white">{formatDate(subscription.currentPeriodEnd)}</p>
                </div>
              )}
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/25 rounded-xl">
                <p className="text-yellow-400 text-sm">
                  ⚠️ Your subscription is scheduled to cancel at the end of the current billing period.
                </p>
              </div>
            )}
          </div>

          {/* Plan Switcher — only for Stripe-billed accounts */}
          {!subscription.managedDirectly && (
            <div className="floating-card">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white">Change Plan</h2>
                <p className="text-white/50 text-sm mt-1">
                  Upgrades are prorated and charged immediately. Downgrades refund unused days to your original payment method and take effect right away.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {PLANS.map((plan, idx) => {
                  const isCurrent = plan.key === currentPlanKey;
                  const isUpgrade = currentPlanIndex !== -1 && idx > currentPlanIndex;
                  const isDowngrade = currentPlanIndex !== -1 && idx < currentPlanIndex;
                  const isChangeable = isUpgrade || isDowngrade;
                  const isLoading = previewLoading === plan.key;

                  return (
                    <div
                      key={plan.key}
                      className={`relative rounded-2xl border p-5 flex flex-col transition-all duration-200 ${
                        isCurrent
                          ? 'border-[#22d3ee] bg-[#22d3ee]/10 ring-1 ring-[#22d3ee]/30'
                          : isChangeable
                          ? 'border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5'
                          : 'border-white/5 bg-white/2'
                      }`}
                    >
                      {isCurrent && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-[#22d3ee] text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                            Current Plan
                          </span>
                        </div>
                      )}

                      <div className="mb-4 mt-2">
                        <h3 className="text-lg font-semibold text-white">{plan.label}</h3>
                        <p className="text-white/50 text-xs mt-0.5">{plan.description}</p>
                      </div>

                      <div className="mb-5">
                        <span className="text-3xl font-bold text-white">{formatCents(plan.price)}</span>
                        <span className="text-white/40 text-sm">/mo</span>
                      </div>

                      <ul className="space-y-2 mb-6 flex-1">
                        {plan.features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                            <CheckIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isCurrent ? 'text-[#22d3ee]' : 'text-white/30'}`} />
                            {feat}
                          </li>
                        ))}
                      </ul>

                      {isCurrent ? (
                        <div className="w-full text-center py-2 text-[#22d3ee] text-sm font-medium">
                          ✓ Active
                        </div>
                      ) : isUpgrade ? (
                        <button
                          onClick={() => handlePreviewPlanChange(plan.key)}
                          disabled={!!previewLoading}
                          className="w-full py-2.5 rounded-xl bg-[#22d3ee] hover:bg-[#6d4fd4] disabled:opacity-60 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isLoading ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Calculating…
                            </>
                          ) : (
                            <>
                              <ArrowUpCircleIcon className="w-4 h-4" />
                              Upgrade
                            </>
                          )}
                        </button>
                      ) : isDowngrade ? (
                        <button
                          onClick={() => handlePreviewPlanChange(plan.key)}
                          disabled={!!previewLoading}
                          className="w-full py-2.5 rounded-xl border border-white/15 hover:border-white/25 hover:bg-white/5 disabled:opacity-60 text-white/70 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isLoading ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Calculating…
                            </>
                          ) : (
                            <>
                              <ArrowDownCircleIcon className="w-4 h-4" />
                              Downgrade
                            </>
                          )}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Stripe Portal link for payment method / invoices */}
              <div className="mt-6 pt-6 border-t border-white/8 flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Payment method &amp; billing history</p>
                  <p className="text-white/40 text-xs mt-0.5">Update your card or view past Stripe invoices</p>
                </div>
                <button
                  onClick={handleManageSubscription}
                  disabled={managingPortal}
                  className="btn-secondary flex items-center gap-2 text-sm"
                >
                  {managingPortal ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Opening…
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="w-4 h-4" />
                      Billing Portal
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Directly managed plan note */}
          {subscription.managedDirectly && (
            <div className="floating-card">
              <h2 className="text-xl font-semibold text-white mb-2">Plan Management</h2>
              <p className="text-white/50">
                Your plan is managed directly by BlockBoard. Contact support to request any changes.
              </p>
              <a href="mailto:support@blockboardapp.com" className="inline-block mt-4 text-[#22d3ee] hover:text-[#9f7fff] font-medium">
                Contact Support →
              </a>
            </div>
          )}

          {/* Plan History */}
          {!subscription.managedDirectly && (
            <div className="floating-card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-white">Plan History</h2>
                  <p className="text-white/50 text-sm mt-1">Your recent plan changes and refunds</p>
                </div>
                {historyLoading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#22d3ee]" />
                )}
              </div>

              {!historyLoading && planHistory.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="w-10 h-10 text-white/15 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No plan changes yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {planHistory.map((event, idx) => {
                    const isDowngrade = event.type === 'downgrade';
                    const isUpgrade   = event.type === 'upgrade';
                    const isInitial   = event.type === 'initial';

                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-4 px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/8"
                      >
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          isUpgrade   ? 'bg-[#22d3ee]/15 text-[#22d3ee]' :
                          isDowngrade ? 'bg-amber-500/15 text-amber-400'  :
                                        'bg-white/10 text-white/50'
                        }`}>
                          {isUpgrade   ? <ArrowUpCircleIcon   className="w-4 h-4" /> :
                           isDowngrade ? <ArrowDownCircleIcon className="w-4 h-4" /> :
                                         <CheckCircleIcon     className="w-4 h-4" />}
                        </div>

                        {/* Label */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isInitial ? (
                              <span className="text-sm font-medium text-white">
                                Subscribed to {getPlanLabel(event.toPlan || '')}
                              </span>
                            ) : (
                              <>
                                <span className="text-sm font-medium text-white/70">
                                  {getPlanLabel(event.fromPlan || '')}
                                </span>
                                <ArrowRightIcon className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                                <span className={`text-sm font-semibold ${isUpgrade ? 'text-[#22d3ee]' : 'text-amber-400'}`}>
                                  {getPlanLabel(event.toPlan || '')}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  isUpgrade
                                    ? 'bg-[#22d3ee]/10 text-[#22d3ee]'
                                    : 'bg-amber-500/10 text-amber-400'
                                }`}>
                                  {isUpgrade ? 'Upgrade' : 'Downgrade'}
                                </span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-white/35 mt-0.5">
                            {formatDate(event.date)}
                          </p>
                        </div>

                        {/* Refund badge */}
                        {isDowngrade && event.refundAmount != null && event.refundAmount > 0 && (
                          <div className="flex-shrink-0 text-right">
                            <p className="text-sm font-semibold text-emerald-400">
                              +{formatCents(event.refundAmount)}
                            </p>
                            <p className="text-xs text-white/35">refunded</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Confirmation Modal ──────────────────────────────────────────────── */}
      {showConfirmModal && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#16161a] border border-white/15 rounded-2xl shadow-2xl w-full max-w-md p-8">

            {preview.isUpgrade ? (
              /* ── Upgrade modal ── */
              <>
                <h2 className="text-xl font-bold text-white mb-1">Confirm Upgrade</h2>
                <p className="text-white/50 text-sm mb-6">
                  Review the prorated charge before confirming.
                </p>

                <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">From</span>
                    <span className="text-white font-medium">{getPlanLabel(preview.currentPlanName)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">To</span>
                    <span className="text-[#22d3ee] font-semibold">{getPlanLabel(preview.newPlanName)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between text-sm">
                    <span className="text-white/50">New monthly price</span>
                    <span className="text-white font-medium">{formatCents(preview.newMonthlyPrice)}/mo</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <div>
                      <p className="text-white font-semibold">Charged today</p>
                      <p className="text-white/40 text-xs mt-0.5">Prorated for remaining billing period</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCents(preview.proratedAmount ?? 0)}</p>
                  </div>
                  {preview.nextBillingDate && (
                    <div className="flex justify-between text-xs text-white/40">
                      <span>Next full charge on</span>
                      <span>{formatDate(preview.nextBillingDate)}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* ── Downgrade modal ── */
              <>
                <h2 className="text-xl font-bold text-white mb-1">Confirm Downgrade</h2>
                <p className="text-white/50 text-sm mb-6">
                  You'll only pay for the days you used. Any surplus is refunded immediately.
                </p>

                <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">From</span>
                    <span className="text-white font-medium">{getPlanLabel(preview.currentPlanName)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">To</span>
                    <span className="text-amber-400 font-semibold">{getPlanLabel(preview.newPlanName)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between text-sm">
                    <span className="text-white/50">New monthly price</span>
                    <span className="text-white font-medium">{formatCents(preview.newMonthlyPrice)}/mo</span>
                  </div>

                  {/* Day-based breakdown */}
                  <div className="border-t border-white/10 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Billing period</span>
                      <span className="text-white">{preview.daysInPeriod} days total</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">
                        {preview.daysUsed} days on {getPlanLabel(preview.currentPlanName)}
                      </span>
                      <span className="text-white">
                        {formatCents(preview.oldDailyRateCents ?? 0)}/day × {preview.daysUsed}d = {formatCents(preview.oldPlanCostCents ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">
                        {preview.daysRemaining} days on {getPlanLabel(preview.newPlanName)}
                      </span>
                      <span className="text-white">
                        {formatCents(preview.newDailyRateCents ?? 0)}/day × {preview.daysRemaining}d = {formatCents(preview.newPlanCostCents ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-white/10 pt-2">
                      <span className="text-white/50">Total fair cost</span>
                      <span className="text-white font-medium">{formatCents(preview.totalFairCostCents ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Amount already paid</span>
                      <span className="text-white">{formatCents(preview.amountPaid ?? 0)}</span>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <div>
                      <p className="text-white font-semibold">
                        {(preview.refundAmount ?? 0) > 0 ? 'Refund' : 'No refund due'}
                      </p>
                      <p className="text-white/40 text-xs mt-0.5">
                        {(preview.refundAmount ?? 0) > 0
                          ? preview.refundIsAutomatic
                            ? 'Processed to your original payment method (5–10 business days)'
                            : 'Our team will process this refund manually within 1–2 business days'
                          : "You've used more than the new plan's value for this period"}
                      </p>
                    </div>
                    <p className={`text-2xl font-bold ${(preview.refundAmount ?? 0) > 0 ? 'text-emerald-400' : 'text-white/40'}`}>
                      {(preview.refundAmount ?? 0) > 0 ? formatCents(preview.refundAmount!) : '$0.00'}
                    </p>
                  </div>

                  {preview.nextBillingDate && (
                    <div className="flex justify-between text-xs text-white/40">
                      <span>Next billing date</span>
                      <span>{formatDate(preview.nextBillingDate)}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {changeError && (
              <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {changeError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirmModal(false); setChangeError(null); setPreview(null); }}
                disabled={changing}
                className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPlanChange}
                disabled={changing}
                className={`flex-1 py-3 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                  preview.isUpgrade
                    ? 'bg-[#22d3ee] hover:bg-[#6d4fd4]'
                    : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {changing ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing…
                  </>
                ) : preview.isUpgrade ? (
                  `Confirm & Pay ${formatCents(preview.proratedAmount ?? 0)}`
                ) : (preview.refundAmount ?? 0) > 0 ? (
                  `Confirm & Refund ${formatCents(preview.refundAmount!)}`
                ) : (
                  'Confirm & Switch Plan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
